import { generateGuid } from "@1js/guid";
import type { InstalledExtension } from "@1js/pl-types";
import type { IPublicClientApplication } from "@azure/msal-browser";
import { buildCoreHeaders } from "../utils/header";
import {
  sendResponseReceivedEvent,
  type SendApiActionEventParameter,
} from "../utils/Telemetry";
import type { HttpStatusCode } from "../utils/Telemetry/config";
import { logApiError } from "./client-utils";
import { authAwareFetch, retry } from "./fetch";

/**
 * Logical ID header key used to identify 3s API calls.
 * See: https://docs.substrate.microsoft.net/docs/Substrate-Intelligence/MSAI-Data-Platform/Contracts/SearchInstrumentationOverview/ContractOverview.html
 */
export const LogicalIdHeaderKey = "x-logical-id";

/**
 * Calls 3s user configuration api to get a list of installed plugins and declarative copilots.
 *
 * @param host service host
 * @param locale requested locale
 * @param is3SServiceEnabled optional is3SServiceEnabled flag
 * @returns Returns a list of installed extensions.
 */
export const fetchInstalledExtensions = async (
  host: string,
  locale: string,
  onSendApiAction: (params: SendApiActionEventParameter) => void,
  msalContext?: IPublicClientApplication
): Promise<InstalledExtension[]> => {
  const installedExtensionsRoute = "installedextensions";
  const logicalId = generateGuid();
  onSendApiAction({
    apiName: "GetInstalledExtensions",
    isInit: true,
    traceId: logicalId,
  });

  const startTimer = Date.now();
  const headers = await buildCoreHeaders(msalContext, logicalId);

  try {
    const serviceUrl = `https://${host}/${locale}/api/usercopilotextensions/${installedExtensionsRoute}`;
    const response = await retry(() =>
      authAwareFetch(serviceUrl, {
        method: "GET",
        headers,
      })
    );

    sendResponseReceivedEvent(
      parseInt(response.headers.get("x-datasource-latency") ?? "0", 10),
      parseInt(response.headers.get("x-datasource-statuscode") ?? "0", 10),
      response.headers.get("x-datasource-traceid") ?? "",
      msalContext
    );

    const installedExtensions = (await response.json()) as InstalledExtension[];

    onSendApiAction({
      apiName: "GetInstalledExtensions",
      status: response.status as HttpStatusCode,
      isInit: false,
      isFromCache: false,
      substrateLatency: parseInt(
        response.headers.get("x-datasource-latency") ?? "0",
        10
      ),
      supportServiceLatency: Date.now() - startTimer,
      successfulResultCount: installedExtensions.length,
      traceId: response.headers.get("x-datasource-traceid") || logicalId,
    });

    return installedExtensions;
  } catch (error) {
    logApiError(
      "GetInstalledExtensions",
      error as Error,
      logicalId,
      Date.now() - startTimer,
      onSendApiAction
    );

    throw error;
  }
};
